import { createSelector } from 'reselect'
import { IS_DUAL, IS_ELEC, IS_GAS, IS_PARTICULAR, IS_PROFESSIONAL, IS_PROPANE } from './Common/constants'
import { parseTab } from '@/shared/tools/activeUtil'
import { capitalize } from '@/shared/tools/stringFunctions'

export const selectSubscriptionForm = state => state.formV2
// USED ONES
// example to fetch a given data: useSelector(selectAdditionalSaleDataByName('voltalis'))
export const selectAdditionalSaleDataByName = name => state => state.formV2.additionalSales[name] || {}
export const selectAllAdditionalSaleData = state => state.formV2.additionalSales || {}
export const selectAdditionalSalesConfigByName = name => state => state.formV2.additionalSalesConfig.sales[name] || {}
export const selectAdditionalSalesConfig = state => state.formV2.additionalSalesConfig || {}
export const selectAddressById = addressId => state => state.formV2.addresses[addressId] || {}
export const selectSubscriptionFormData = createSelector(selectSubscriptionForm, data => data)
export const selectOpenedContracts = createSelector(selectSubscriptionForm, data => data.openedContracts)
export const selectExistingContracts = createSelector(selectSubscriptionForm, data => data.contracts)
export const selectGroupedContractsByDate = createSelector(selectExistingContracts, contracts => {
  const groups = {}

  Object.values(contracts || {}).forEach(contract => {
    if (!contract?.createdAt) return
    const date = contract.createdAt.split(' ')[0]
    groups[date] = groups[date] || []
    groups[date].push(contract)
  })

  return Object.entries(groups)
    .map(([date, contracts]) => ({ date, contracts }))
    .sort((a, b) => new Date(a.date) - new Date(b.date))
})
export const selectClientSubscriptionData = createSelector(selectSubscriptionForm, data => data.client)
export const selectContactComments = createSelector(selectSubscriptionForm, data => data.comments)
export const selectPersonalClientSubscriptionData = createSelector(selectSubscriptionForm, data => data.client.personal)
export const selectPreferencesClientSubscriptionData = createSelector(
  selectSubscriptionForm,
  data => data.client.preferences,
)
export const selectProfessionalClientSubscriptionData = createSelector(
  selectSubscriptionForm,
  data => data.client.professional,
)
export const selectClientConfig = createSelector(selectSubscriptionForm, data => data.client.config)
export const selectClientFormErrors = createSelector(selectSubscriptionForm, data => data.client.config.errors ?? {})
export const selectAdditionalClientSubscriptionData = createSelector(
  selectSubscriptionForm,
  data => data.client.additional,
)
export const selectConfig = createSelector(selectSubscriptionForm, data => data.config)
export const selectActiveTab = createSelector(selectConfig, data => data.activeTab ?? '')
export const selectActiveAddress = createSelector(selectConfig, data => data.activeAddress)
export const selectActiveTabParts = createSelector(selectActiveTab, activeTab => parseTab(activeTab))
export const selectActiveTabId = createSelector(selectActiveTabParts, parts => parts.id)
export const selectActiveTabType = createSelector(selectActiveTabParts, parts => capitalize(parts.type))
export const selectCurrentContractActive = createSelector(
  selectActiveTabId,
  selectExistingContracts,
  (activeTabId, contracts) => contracts[activeTabId] ?? {},
)

export const selectCharacteristicsCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.characteristics ?? {},
)

export const selectConsumptionCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.consumption ?? {},
)

export const selectContractDetailsCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.contractDetails ?? {},
)

export const selectBillingCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.billing ?? {},
)

export const selectElecCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.elec ?? {},
)

export const selectGasCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.gas ?? {},
)

export const selectPropaneCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.propane ?? {},
)

export const selectConfigCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.config ?? {},
)

export const selectOfferConfigCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.config?.offerConfiguration ?? {},
)

export const selectProviderConfigCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.config?.providerConfiguration ?? {},
)

export const selectOfferAttributesCurrentContractActive = createSelector(
  selectCurrentContractActive,
  currentContract => currentContract?.config?.offerConfiguration?.attributes ?? {},
)

export const isParticularClient = createSelector(selectPreferencesClientSubscriptionData, preferences => {
  const clientType = preferences.clientType ?? false
  return clientType === IS_PARTICULAR
})

export const isProfessionalClient = createSelector(selectPreferencesClientSubscriptionData, preferences => {
  const clientType = preferences.clientType ?? false
  return clientType === IS_PROFESSIONAL
})

export const isGasContract = createSelector(selectCurrentContractActive, currentContract => {
  const energyType = currentContract?.characteristics?.energyType ?? false
  return energyType === IS_GAS
})

export const isElecContract = createSelector(selectCurrentContractActive, currentContract => {
  const energyType = currentContract?.characteristics?.energyType ?? false
  return energyType === IS_ELEC
})

export const isDualContract = createSelector(selectCurrentContractActive, currentContract => {
  const energyType = currentContract?.characteristics?.energyType ?? false
  return energyType === IS_DUAL
})

export const isPropaneContract = createSelector(selectCurrentContractActive, currentContract => {
  const energyType = currentContract?.characteristics?.energyType ?? false
  return energyType === IS_PROPANE
})

export const isGasOrDualContract = createSelector(isGasContract, isDualContract, (isGasContract, isDualContract) => {
  return isGasContract || isDualContract
})

export const selectAddressesData = createSelector(selectSubscriptionForm, data => data.addresses ?? {})

export const selectAddressCurrentContractActive = createSelector(
  selectCurrentContractActive,
  selectAddressesData,
  (currentContract, addreses) => addreses[currentContract?.addressId] ?? {},
)

export const selectAddressesConfigData = createSelector(selectAddressesData, data => data.config ?? {})
export const selectAddressesErrorsData = createSelector(selectAddressesConfigData, data => data.errors ?? {})
export const selectFormattedAddressList = createSelector(selectAddressesConfigData, data => {
  const addressList = data.addressList ?? []
  return addressList.map(address => ({
    id: address.label,
    label: address.label,
    address: address.streetName,
    streetName: address.streetName,
    streetNumber: address.houseNumber,
    houseNumber: address.houseNumber,
    city: address.city,
    inseeCode: address.inseeCode,
    zipcode: address.zipcode,
    type: address.type,
    context: address.context,
  }))
})
export const selectCurrentAddressData = createSelector(
  selectActiveAddress,
  selectAddressesData,
  (activeAddress, addresses) => addresses[activeAddress ?? 'new'] ?? {},
)
export const selectCurrentAddressErrors = createSelector(
  selectActiveAddress,
  selectAddressesErrorsData,
  (activeAddress, addressesErrorsData) => addressesErrorsData[activeAddress] ?? {},
)

export const selectCityEligibilityData = createSelector(
  selectAddressCurrentContractActive,
  selectAddressesData,
  selectAddressesConfigData,
  (currentContractAddress, addresses, addressesConfig) => {
    return {
      cityEligibility: currentContractAddress?.cityEligibility ?? {},
      cityEligibilityIsLoading: addressesConfig?.addressCityEligibilityIsLoading ?? false,
    }
  },
)

export const selectPdlSearchData = createSelector(selectSubscriptionForm, data => data.pdlSearchData ?? {})
export const selectPceSearchData = createSelector(selectSubscriptionForm, data => data.pceSearchData ?? {})
export const selectCommunicationBoardState = createSelector(
  selectSubscriptionForm,
  form => form?.communicationBoard || {},
)

export const selectScheduledCallbackState = createSelector(
  selectSubscriptionForm,
  form => form?.scheduledCallback || {},
)

export const selectScheduledCallbackForm = createSelector(
  selectScheduledCallbackState,
  callback => callback?.form || {},
)

export const selectScheduledCallbackUpcoming = createSelector(
  selectScheduledCallbackState,
  callback => callback?.upcoming || {},
)
export const selectHasScheduledCallbackUpcoming = createSelector(
  selectScheduledCallbackUpcoming,
  upcoming => Array.isArray(upcoming.items) && upcoming.items.length > 0,
)

export const selectClubSelectraState = createSelector(selectSubscriptionForm, form => form?.clubSelectra || {})
